import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { markPageVisited } from '../../../../api/patient.api';
import { useAuth } from '../../../../context/auth-context';
import './Tka.scss';

type YoutubeVideo = {
    title: string;
    subtitle: string;
    link: string;
    image: string;
}

const videoGallery: YoutubeVideo[] = [
    {
        title: 'Total Knee Replacement Patient Animation',
        subtitle: 'Main educational video explaining the TKA procedure',
        link: 'https://www.youtube.com/watch?v=tLxrEokzRZ4',
        image: 'https://img.youtube.com/vi/tLxrEokzRZ4/mqdefault.jpg',
    },
    {
        title: 'How long is the recovery from knee replacement surgery?',
        subtitle: 'Detailed recovery timeline and expectations',
        link: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
        image: 'https://img.youtube.com/vi/9bZkp7q19f0/mqdefault.jpg',
    },
    {
        title: '5 Mistakes to Avoid after Total Knee Replacement',
        subtitle: 'Common pitfalls and how to prevent them',
        link: 'https://www.youtube.com/watch?v=jNQXAC9IVRw',
        image: 'https://img.youtube.com/vi/jNQXAC9IVRw/mqdefault.jpg',
    },
    {
        title: 'Physical Therapy After Knee Replacement',
        subtitle: 'Essential exercises and rehabilitation guidance',
        link: 'https://www.youtube.com/watch?v=UX-BrTv7i8E',
        image: 'https://img.youtube.com/vi/UX-BrTv7i8E/mqdefault.jpg',
    },
    {
        title: 'Total Knee Replacement Patient Animation',
        subtitle: 'Main educational video explaining the TKA procedure',
        link: 'https://www.youtube.com/watch?v=tLxrEokzRZ4',
        image: 'https://img.youtube.com/vi/tLxrEokzRZ4/mqdefault.jpg',
    },
    {
        title: 'Total Knee Replacement Patient Animation',
        subtitle: 'Main educational video explaining the TKA procedure',
        link: 'https://www.youtube.com/watch?v=tLxrEokzRZ4',
        image: 'https://img.youtube.com/vi/tLxrEokzRZ4/mqdefault.jpg',
    },
];

const resourceLinks = [
    {
        title: 'American Academy of Orthopaedic Surgeons. (2024). Total Knee Replacement.',
        link: 'https://orthoinfo.aaos.org/en/treatment/total-knee-replacement',
    },
    {
        title: 'Mayo Clinic. (2024). Knee Replacement Surgery.',
        link: 'https://www.mayoclinic.org/tests-procedures/knee-replacement/about/pac-20385276',
    },
    {
        title: 'Johns Hopkins Medicine. (2024). Knee Replacement Surgery Procedure.',
        link: 'https://www.hopkinsmedicine.org/health/treatment-tests-and-therapies/knee-replacement-surgery-procedure',
    },
    {
        title: 'Cleveland Clinic. (2024). Total Knee Replacement.',
        link: 'https://my.clevelandclinic.org/health/treatments/8512-total-knee-replacement',
    },
    {
        title: 'National Institutes of Health - MedlinePlus. (2024). Knee Replacement.',
        link: 'https://medlineplus.gov/kneereplacement.html',
    },
];

const scrollAmount = 240;
let currentIndex = 0;
const cardsToShow = 3;
const totalCards = 5;
const totalSlides = totalCards - cardsToShow + 1;

const Tka = () => {
    const navigate = useNavigate();
    const { isAuthLoading, isAuthenticated, userInfo } = useAuth();
    const galleryScrollRef = useRef<HTMLDivElement>(null);
    const benefitsRef = useRef<HTMLDivElement>(null);
    const [ galleryPrevBtnDisabled, setPrevBtnDisabled ] = useState(true);
    const [ galleryNextBtnDisabled, setNextBtnDisabled ] = useState(false);
    const [ sliderPrevBtnDisabled, setSliderPrevBtnDisabled ] = useState(true);
    const [ sliderNextBtnDisabled, setSliderNextBtnDisabled ] = useState(false);
    const [ activeRiskTab, setActiveRiskTab ] = useState<'short-term' | 'long-term' | 'other'>('short-term');
    const isRedirectedAfterLogin = useRef(false);

    useEffect(() => {
        if (isAuthLoading || !isAuthenticated) {
            return;
        }

        if (isRedirectedAfterLogin.current) {
            return;
        }

        const savedURL = localStorage.getItem('preLoginURL') || '';
        localStorage.removeItem('preLoginURL');
        if (savedURL) {
            navigate(savedURL);
            isRedirectedAfterLogin.current = true;
            return;
        }

        if (userInfo?.role === 'patient') {
            markPageVisited({ pageName: 'tka', pageType: 'treatment' })
            .then(() => {
            });
        }

        isRedirectedAfterLogin.current = true;
    }, [ isAuthLoading, isAuthenticated, navigate, userInfo ]);

    const onGalleryScroll = (direction: 'left' | 'right') => {
        if (!galleryScrollRef.current) {
            return;
        }

        const sign = direction === 'left' ? -1 : 1;

        galleryScrollRef.current.scrollBy({
            left: sign * scrollAmount,
            behavior: 'smooth',
        });
    };

    const onGalleryScrollListener = useCallback(() => {
        if (!galleryScrollRef.current) {
            return;
        }

        const isAtStart = galleryScrollRef.current.scrollLeft === 0;
        const isAtEnd = galleryScrollRef.current.scrollLeft >= galleryScrollRef.current.scrollWidth - galleryScrollRef.current.clientWidth;

        setPrevBtnDisabled(isAtStart);
        setNextBtnDisabled(isAtEnd);
    }, []);

    const updateSlider = () => {
        if (!benefitsRef.current) {
            return;
        }

        // @ts-ignore
        const cards: HTMLDivElement[] = document.querySelectorAll('.benefit-card');
        const dots = document.querySelectorAll('.benefits-dots .dot');

        const cardWidth = cards[0].offsetWidth;
        const gap = parseInt(window.getComputedStyle(benefitsRef.current).gap);
        const moveDistance = cardWidth + gap;
        benefitsRef.current.style.transform = `translateX(-${currentIndex * moveDistance}px)`;

        setSliderPrevBtnDisabled(currentIndex === 0);
        setSliderNextBtnDisabled(currentIndex >= totalSlides - 1);

        dots.forEach(dot => dot.classList.remove('active'));
        if (dots[currentIndex]) {
            dots[currentIndex].classList.add('active');
        }
    };

    const onPrevSliderClick = () => {
        if (currentIndex > 0) {
            currentIndex--;
            updateSlider();
        }
    };

    const onNextSliderClick = () => {
        if (currentIndex < totalSlides - 1) {
            currentIndex++;
            updateSlider();
        }
    };

    const onDotClick = (index: number) => {
        currentIndex = index;
        updateSlider();
    };

    const setupPdfDownload = () => {
        const downloadBtn = document.getElementById('download-pdf-btn');
        if (downloadBtn) {
            downloadBtn.addEventListener('click', () => {
                // @ts-ignore
                const { jsPDF } = window.jspdf;
                const checklist = document.getElementById('checklist-to-print');

                // @ts-ignore
                html2canvas(checklist, {
                    scale: 2, // Higher scale for better quality
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    // @ts-ignore
                }).then(canvas => {
                    const imgData = canvas.toDataURL('image/png');
                    const pdf = new jsPDF({
                        orientation: 'p',
                        unit: 'mm',
                        format: 'a4',
                    });

                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const canvasWidth = canvas.width;
                    const canvasHeight = canvas.height;
                    const ratio = canvasWidth / canvasHeight;
                    const imgWidth = pdfWidth - 20; // with some margin
                    const imgHeight = imgWidth / ratio;

                    let position = 10;
                    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
                    pdf.save('TKA_Pre-Surgery_Checklist.pdf');
                });
            });
        }
    };

    useEffect(() => {
        if (!galleryScrollRef.current) {
            return;
        }
        const localGalleryScrollRef = galleryScrollRef.current;

        localGalleryScrollRef.addEventListener('scroll', onGalleryScrollListener);

        return () => localGalleryScrollRef.removeEventListener('scroll', onGalleryScrollListener);
    }, [ onGalleryScrollListener ]);

    return (
        <div className="tka">
            <div className="chatbot-button" id="chatbot-button">
                <i className="fas fa-question-circle"></i>
                <span className="chatbot-tooltip">Ask me about TKA</span>
            </div>

            <h1 className="page-title">Total Knee Arthroplasty (TKA)</h1>

            <section className="video-intro-section" id="video-introduction">
                <div className="container">
                    <div className="video-intro-content">
                        <div className="video-intro-player">
                            <iframe
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                id="main-video"
                                src="https://www.youtube.com/embed/tLxrEokzRZ4"
                                title="Total Knee Replacement Patient Animation">
                            </iframe>
                        </div>
                        <div className="video-intro-text">
                            <div className="overview-text">
                                <p className="lead">
                                    Total Knee Arthroplasty (TKA) is a surgical procedure used to treat severe knee
                                    joint damage, most commonly caused by advanced osteoarthritis.
                                </p>
                                <p>
                                    In this procedure, the surgeon removes the worn-out or damaged surfaces of the knee
                                    and replaces
                                    them with artificial components made of metal and high-grade plastic, designed to
                                    mimic the
                                    movement and function of a healthy knee.
                                </p>
                                <p>
                                    This treatment is typically recommended for individuals who suffer from chronic knee
                                    pain,
                                    stiffness, and loss of mobility that interfere with everyday activities—such as
                                    walking,
                                    standing, or climbing stairs—and who have not improved with non-surgical options
                                    like physical
                                    therapy, medications, or joint injections.
                                </p>
                                <p>
                                    TKA can also be performed for patients with rheumatoid arthritis, prior fractures,
                                    or
                                    deformities
                                    that affect knee function. The primary goals are to relieve pain, restore knee
                                    motion, and
                                    improve quality of life. For many patients, TKA allows them to return to activities
                                    they had
                                    given up due to pain.
                                </p>
                                <p>
                                    TKA is considered one of the most successful orthopedic procedures, with high
                                    patient
                                    satisfaction and a long track record of effectiveness. Most people experience
                                    significant
                                    improvement in pain and function, and modern implants often last 15 to 20 years or
                                    more.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="video-gallery">
                        <div className="gallery-container">
                            <div className="gallery-scroll" ref={galleryScrollRef}>
                                {videoGallery.map((item) => (
                                    <Link
                                        className="video-thumbnail"
                                        key={item.link}
                                        to={item.link}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <div className="thumbnail-image">
                                            <img
                                                alt={item.title}
                                                src={item.image}
                                            />
                                            <div className="play-overlay">
                                                <i className="fas fa-play"></i>
                                            </div>
                                        </div>
                                        <div className="thumbnail-info">
                                            <h4>{item.title}</h4>
                                            <p>{item.subtitle}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            <button
                                className="gallery-nav prev"
                                onClick={() => onGalleryScroll('left')}
                                disabled={galleryPrevBtnDisabled}
                            >
                                <i className="fas fa-chevron-left"/>
                            </button>
                            <button
                                className="gallery-nav next"
                                onClick={() => onGalleryScroll('right')}
                                disabled={galleryNextBtnDisabled}
                            >
                                <i className="fas fa-chevron-right"/>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="benefits-section">
                <div className="container">
                    <h2 className="section-title">Benefits of TKA</h2>
                    <div className="benefits-slider-container">
                        <div className="benefits-grid" ref={benefitsRef}>
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="fas fa-heart"></i>
                                </div>
                                <h3>Pain Relief</h3>
                                <p>Most patients experience significant to complete relief from chronic knee pain.</p>
                            </div>
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="fas fa-walking"></i>
                                </div>
                                <h3>Improved Function</h3>
                                <p>Walking, standing, climbing stairs, and participating in light physical activity
                                    becomes
                                    easier.</p>
                            </div>
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="fas fa-smile"></i>
                                </div>
                                <h3>Enhanced Quality of Life</h3>
                                <p>Greater independence and ability to enjoy everyday activities.</p>
                            </div>
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="fas fa-calendar-check"></i>
                                </div>
                                <h3>Long-Term Effectiveness</h3>
                                <p>Modern implants last 15–20 years or longer, depending on activity level and general
                                    health.</p>
                            </div>
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="fas fa-chart-line"></i>
                                </div>
                                <h3>High Success Rates</h3>
                                <p>TKA is widely performed with strong evidence of safety and satisfaction.</p>
                            </div>
                        </div>
                    </div>
                    <div className="benefits-controls">
                        <button
                            className="slider-nav prev"
                            onClick={onPrevSliderClick}
                            disabled={sliderPrevBtnDisabled}
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <div className="benefits-dots">
                            <span
                                className={`dot ${currentIndex === 0 ? 'active' : ''}`}
                                onClick={() => onDotClick(0)}
                            />
                            <span
                                className={`dot ${currentIndex === 1 ? 'active' : ''}`}
                                onClick={() => onDotClick(1)}
                            />
                            <span
                                className={`dot ${currentIndex === 2 ? 'active' : ''}`}
                                onClick={() => onDotClick(2)}
                            />
                        </div>
                        <button
                            className="slider-nav next"
                            onClick={onNextSliderClick}
                            disabled={sliderNextBtnDisabled}
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </section>

            <section className="risks-section" id="risks">
                <div className="container">
                    <h2 className="section-title">Risks and Side Effects</h2>
                    <p className="section-subtitle">While TKA is generally safe, it is still a major surgery, and
                        complications—though
                        rare—can occur. Understanding the risks helps patients make informed decisions.</p>

                    <div className="risks-tabs">
                        <div className="tab-buttons">
                            <button
                                className={`tab-btn ${activeRiskTab === 'short-term' ? 'active' : ''}`}
                                onClick={() => setActiveRiskTab('short-term')}
                            >
                                Short-Term Risks
                            </button>
                            <button
                                className={`tab-btn ${activeRiskTab === 'long-term' ? 'active' : ''}`}
                                onClick={() => setActiveRiskTab('long-term')}
                            >
                                Mid/Long-Term Risks
                            </button>
                            <button
                                className={`tab-btn ${activeRiskTab === 'other' ? 'active' : ''}`}
                                onClick={() => setActiveRiskTab('other')}
                            >
                                Other Considerations
                            </button>
                        </div>

                        <div className={`tab-content ${activeRiskTab === 'short-term' ? 'active' : ''}`}>
                            <div className="risk-list">
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Infection at the surgical site</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Blood clots in the legs or lungs</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Bleeding or wound complications</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Temporary nerve pain or numbness</span>
                                </div>
                            </div>
                        </div>

                        <div className={`tab-content ${activeRiskTab === 'long-term' ? 'active' : ''}`}>
                            <div className="risk-list">
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Stiffness or reduced knee motion</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Loosening or wear of the implant over time</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Instability, clicking, or grinding sensations</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>In rare cases, revision surgery may be required</span>
                                </div>
                            </div>
                        </div>

                        <div className={`tab-content ${activeRiskTab === 'other' ? 'active' : ''}`}>
                            <div className="risk-list">
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Patellar issues or tracking problems</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Allergic reactions to metal components (rare)</span>
                                </div>
                                <div className="risk-item">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Persistent swelling or discomfort in some patients</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="timeline-section" id="timeline">
                <div className="container">
                    <h2 className="section-title">What to Expect – Before, During, and After</h2>
                    <div className="timeline">
                        <div className="timeline-item">
                            <div className="timeline-marker">
                                <i className="fas fa-calendar-alt"></i>
                            </div>
                            <div className="timeline-content">
                                <h3>Before Surgery</h3>
                                <ul>
                                    <li>You'll undergo medical evaluation, lab tests, and imaging (X-rays) to confirm
                                        your condition
                                        and ensure you're healthy enough for surgery.
                                    </li>
                                    <li>Your doctor may recommend weight loss, quitting smoking, or managing diabetes to
                                        lower
                                        surgical risks.
                                    </li>
                                    <li>You'll receive instructions on how to prepare your home and what to expect
                                        post-op.
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="timeline-item">
                            <div className="timeline-marker">
                                <i className="fas fa-procedures"></i>
                            </div>
                            <div className="timeline-content">
                                <h3>During Surgery</h3>
                                <ul>
                                    <li>The procedure usually takes 1–2 hours under general or spinal anesthesia.</li>
                                    <li>The surgeon removes damaged bone and cartilage, reshapes the surfaces, and
                                        implants the
                                        prosthetic components.
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="timeline-item">
                            <div className="timeline-marker">
                                <i className="fas fa-hospital"></i>
                            </div>
                            <div className="timeline-content">
                                <h3>Immediately After Surgery</h3>
                                <ul>
                                    <li>Hospital stay is typically 1–3 days, though some patients go home the same
                                        day.
                                    </li>
                                    <li>You'll begin gentle physical therapy within 24 hours to start regaining
                                        motion.
                                    </li>
                                    <li>Pain is managed with medications, and you'll use a walker or crutches for
                                        support.
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="timeline-item">
                            <div className="timeline-marker">
                                <i className="fas fa-road"></i>
                            </div>
                            <div className="timeline-content">
                                <h3>Recovery Timeline</h3>
                                <div className="recovery-phases">
                                    <div className="phase">
                                        <h4>Weeks 1–2</h4>
                                        <p>Focus on pain control and walking with assistance</p>
                                    </div>
                                    <div className="phase">
                                        <h4>Weeks 3–6</h4>
                                        <p>Physical therapy to rebuild strength and range of motion</p>
                                    </div>
                                    <div className="phase">
                                        <h4>3–6 Months</h4>
                                        <p>Most patients resume normal daily activities</p>
                                    </div>
                                    <div className="phase">
                                        <h4>6–12 Months</h4>
                                        <p>Full recovery, including return to recreational activities like walking,
                                            biking, or
                                            swimming</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="preparation-section" id="preparation">
                <div className="container">
                    <h2 className="section-title">Get Prepared – Pre-Surgery Checklist</h2>
                    <div className="checklist-container" id="checklist-to-print">
                        <div className="checklist-category">
                            <h3><i className="fas fa-user-md"></i> Medical and Personal Preparation</h3>
                            <div className="checklist">
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Complete pre-op physical and blood tests</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Inform your care team of all medications and allergies</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Discuss anesthesia and pain management options</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Stop smoking and reduce alcohol intake</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Arrange transportation and support at home</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>If applicable, consult specialists (cardiologist, endocrinologist, etc.)</span>
                                </div>
                            </div>
                        </div>

                        <div className="checklist-category">
                            <h3><i className="fas fa-home"></i> Home Safety and Equipment</h3>
                            <div className="checklist">
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Remove rugs, cords, or anything that might cause tripping</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Install handrails in the bathroom or along stairs</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Set up a sleeping area on the ground floor (if needed)</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Prepare meals ahead of time or arrange meal delivery</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Consider a toilet seat riser or shower chair</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Rent or purchase a walker, cane, or crutches</span>
                                </div>
                            </div>
                        </div>

                        <div className="checklist-category">
                            <h3><i className="fas fa-suitcase"></i> What to Pack for the Hospital</h3>
                            <div className="checklist">
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>List of current medications</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Comfortable, loose-fitting clothing and flat shoes</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Personal hygiene items and ID/insurance cards</span>
                                </div>
                                <div className="checkbox-item">
                                    <i className="far fa-square"></i>
                                    <span>Cell phone charger and anything to stay entertained</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="download-cta">
                        <button className="btn-primary" onClick={setupPdfDownload}>
                            <i className="fas fa-download"/>
                            Download as PDF
                        </button>
                    </div>
                </div>
            </section>

            <section className="resources-section" id="resources">
                <div className="container">
                    <h2 className="section-title">Learn More from Trusted Sources</h2>
                    <div className="resources-list">
                        {resourceLinks.map((item) => (
                            <div className="resource-item" key={item.link}>
                                <p>
                                    <a href={item.link} rel="noopener noreferrer" target="_blank">
                                        {item.title}
                                    </a>
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );

};

export default Tka;